import { Minus, TrendingDown, TrendingUp } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { Card } from '@/components/ui/card';
import { IconWell } from '@/components/ui/icon-well';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip } from '@/components/ui/tooltip';
import type { TipId } from '@/tips';
import { cn } from '@/utils/cn';

import { MoneyDisplay } from './money-display';
import { Tip } from './tip';

/**
 * One figure with its comparison.
 *
 * The comparison always names the period it compares against. "+12%" on its own is a
 * number a pastor cannot act on; "+12% against last month" is one they can.
 *
 * Direction is not automatically good or bad. Expenses rising is not a success, so the
 * caller says which direction is favourable.
 */

export interface StatCardProps {
  label: string;
  /** Either a formatted figure or an amount to render through `MoneyDisplay`. */
  value?: ReactNode;
  money?: {
    amount: number;
    currency: string;
    parenthesiseNegative?: boolean;
    colorBySign?: boolean;
  };
  icon?: ReactNode;
  comparison?: {
    /** Signed fraction: 0.12 renders as +12%. */
    change: number;
    /** "against last month" — supplied already translated. */
    label: string;
    /** Whether an increase is good news. Defaults to true. */
    increaseIsGood?: boolean;
    formatted: string;
  };
  loading?: boolean;
  /** Catalog tip shown as a dotted-underline label. Prefer over `hint`. */
  tipId?: TipId;
  /** @deprecated Prefer `tipId`. Extra sentence of context on hover of the label. */
  hint?: string;
  /** When set, the whole card links to this route. */
  to?: string;
  /** Extra content under the comparison — names, not a second figure. */
  footer?: ReactNode;
  className?: string;
}

export const StatCard = ({
  label,
  value,
  money,
  icon,
  comparison,
  loading = false,
  tipId,
  hint,
  to,
  footer,
  className,
}: StatCardProps) => {
  const direction =
    comparison === undefined
      ? 'flat'
      : comparison.change > 0
        ? 'up'
        : comparison.change < 0
          ? 'down'
          : 'flat';

  const good =
    direction === 'flat'
      ? null
      : (comparison?.increaseIsGood ?? true) === (direction === 'up');

  const card = (
    <Card
      className={cn(
        'p-4',
        to && 'h-full transition-colors hover:border-brand/40 hover:bg-surface-sunken/40',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {tipId ? (
            <Tip tipId={tipId} mode="label">
              {label}
            </Tip>
          ) : hint ? (
            <Tooltip content={hint}>
              <p className="cursor-help text-body-sm text-fg-secondary underline decoration-dotted underline-offset-2">
                {label}
              </p>
            </Tooltip>
          ) : (
            <p className="text-body-sm text-fg-secondary">{label}</p>
          )}

          <div className="mt-1.5 min-h-[1.75rem] flex items-center">
            {loading ? (
              <Skeleton className="h-7 w-28" />
            ) : money ? (
              <MoneyDisplay
                amount={money.amount}
                currency={money.currency}
                size="lg"
                parenthesiseNegative={money.parenthesiseNegative}
                colorBySign={money.colorBySign}
              />
            ) : (
              <div className="text-section-title font-semibold tabular-nums text-fg">{value}</div>
            )}
          </div>
        </div>

        {icon ? <IconWell>{icon}</IconWell> : null}
      </div>

      {comparison && !loading ? (
        <p className="mt-3 flex items-center gap-1.5 text-caption">
          <span
            className={cn(
              'inline-flex items-center gap-0.5 font-medium',
              good === null ? 'text-fg-secondary' : good ? 'text-success-fg' : 'text-danger-fg',
            )}
          >
            {direction === 'up' ? (
              <TrendingUp aria-hidden className="h-3.5 w-3.5" />
            ) : direction === 'down' ? (
              <TrendingDown aria-hidden className="h-3.5 w-3.5" />
            ) : (
              <Minus aria-hidden className="h-3.5 w-3.5" />
            )}
            {comparison.formatted}
          </span>
          <span className="text-fg-tertiary">{comparison.label}</span>
        </p>
      ) : null}

      {footer && !loading ? <div className="mt-3">{footer}</div> : null}
    </Card>
  );

  if (to) {
    return (
      <Link
        to={to}
        className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
      >
        {card}
      </Link>
    );
  }

  return card;
};
